import {
    Box,
    Tabs,
    Tab,
    Typography,
    Tooltip,
    Button,
    CircularProgress,
} from "@mui/material";
import {
  ExpandMore,
  ChevronRight,
  Article,
  RadioButtonChecked,
  CheckBox,
  Upload,
  Title,
  ArrowBack,
  Publish,
  Unpublished,
  Save,
  CheckCircleOutline,
  Autorenew
} from '@mui/icons-material';

import React, { memo, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { AssessmentStatus } from "../../../../models/assessment";
import { useSnackbar } from "../../../../components/snackbar";
import Header from "../components/header";
import Settings from "../components/tabs/settings";
import Items from "../components/tabs/items";
import Responses from "../components/tabs/responses";
import FirstPublishDialog from "../components/first-publish-dialog";
import { useClassroomActions } from "../../../../hooks/useClassroomActions";
import BackdropLoader from "../../../../components/backdrop-loader";
import { useDomainActions } from "../../../../hooks/useDomainActions";
import isHubActivated from "../../../../utils/is-hub-activated";
import { truncateString } from "../../../../utils/truncate-string";

type RouteParams = {
    classroom_id: string;
    assessment_id: string;
};

type TabItem = {
  label: string;
  value: string;
};

const tabs: TabItem[] = [
  { label: "Settings", value: "settings" },
  { label: "Questions", value: "questions" },
  { label: "Responses", value: "responses" }
];


function AssessmentDetail() {
    const { classroom_id, assessment_id } = useParams<RouteParams>();

    const { 
        classrooms, 
        getClassrooms, 
        getAssessment, 
        updateAssessmentStatus,
        getAssessments
    } = useClassroomActions();

    const { hub, getHub } = useDomainActions();

    const classroom = classrooms?.[Number(classroom_id)];
    
    let assessment = classroom?.assessments?.[Number(assessment_id)];

    
    const location = useLocation();
    const navigate = useNavigate();

    
    const [currentTab, setCurrentTab] = useState(location.hash.replace("#", "") || "questions");
    
    const [mountedTabs, setMountedTabs] = useState<string[]>([currentTab]);

    const [saved, setSaved] = useState(true);

    const [loading, setLoading] = useState(true);

    const [toggling, setToggling] = useState(false);

    const [firstPublishDialog, setFirstPublishDialog] = useState(false);

    const { showMessage } = useSnackbar();

    const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
        setCurrentTab(newValue);
        navigate(`#${newValue}`);
        
        if (!mountedTabs.includes(newValue)) {
            setMountedTabs((prev) => [...prev, newValue]);
        }
    };


    const handleToggleAssessmentClick = () => {
        if (!assessment) return;

        if (assessment.status === AssessmentStatus.DRAFT) {
            setFirstPublishDialog(true);
        }
        else {
            toggleAssessmentStatus();
        }      
    };

    const toggleAssessmentStatus = async () => {
        if (!assessment) return;

        setToggling(true);

        const newStatus = assessment.status === AssessmentStatus.PUBLISHED ? AssessmentStatus.ARCHIVED : AssessmentStatus.PUBLISHED;

        if (newStatus === AssessmentStatus.PUBLISHED && !isHubActivated(hub)) {
            showMessage('Hub is not activated', 1);
            return;
        }
        
        await updateAssessmentStatus(Number(classroom_id), assessment.id, newStatus);
        
        setToggling(false);

        showMessage(newStatus === AssessmentStatus.PUBLISHED ? "Assessment Published" : "Assessment Archived", 3);
    };

    useEffect(() => {
        setCurrentTab(currentTab);
        if (!mountedTabs.includes(currentTab)) {
            setMountedTabs((prev) => [...prev, currentTab]);
        }
    }, [currentTab]);

    useEffect(() => {
        const loadNeededData = async () => {
            if (!hub) await getHub();
            if (!classrooms) await getClassrooms();
            if (classroom && !classroom.assessments) await getAssessments(Number(classroom_id));
            if (assessment) {
                if (!assessment.questions) await getAssessment(Number(classroom_id), Number(assessment_id));
                setLoading(false);
            }
        };

        loadNeededData();
    }, [classrooms, classroom_id, assessment_id]);

    if (!hub || !classrooms || !classroom || !classroom.assessments || !assessment || loading) {
        return (
            <BackdropLoader/>
        )
    }
    return (
        <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
            
           <Box
                sx={{
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    paddingTop: 2,
                    backdropFilter: 'blur(8px)',
                    position: 'relative',
                }}
            >
                <Box
                    component={Link}
                    to={`/classroom/${classroom_id}#assessments`}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        textDecoration: 'none',
                        color: '#475569',
                        padding: '8px 12px',
                        borderRadius: '8px',
                        transition: 'all 0.2s ease',
                        minWidth: 0,
                    }}
                >
                    <ArrowBack 
                        fontSize="small" 
                        sx={{ 
                            flexShrink: 0,
                            color: '#64748b',
                            transition: 'color 0.2s ease',
                        }} 
                    />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: '0.75rem',
                                fontWeight: 400,
                                color: '#64748b',
                                lineHeight: 1,
                                mb: 0.25,
                            }}
                        >
                            Assessments
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: 600,
                                color: '#1e293b',
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {assessment && truncateString(assessment.title, 20) || "Untitled Assessment"}
                        </Typography>
                    </Box>
                </Box>


                <Box display="flex" alignItems="center" sx={{ flex: 1 }}>
                    {saved ? (
                        <>
                            <CheckCircleOutline fontSize="small" color="action" sx={{ mr: 0.5 }} />
                            <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary' }}
                            >
                                All changes saved
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Autorenew
                                fontSize="small"
                                color="action"
                                sx={{ mr: 0.5, animation: 'spin 1s linear infinite' }}
                            />
                            <Typography
                                variant="caption"
                                sx={{ color: 'text.secondary' }}
                            >
                                Saving...
                            </Typography>
                        </>
                    )}
                </Box>


                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexShrink: 0, marginRight: 2 }}>
        

                    
               <Button
                    size="medium"
                    variant="contained"
                    disabled={toggling}
                    startIcon={
                        toggling ? (
                        <CircularProgress size={18} color="inherit" />
                        ) : (
                        (assessment.status === AssessmentStatus.DRAFT || assessment.status === AssessmentStatus.ARCHIVED) ? (
                            <Publish fontSize="small" />
                        ) : (
                            <Unpublished fontSize="small" />
                        )
                        )
                    }
                    onClick={handleToggleAssessmentClick}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 500,
                        borderRadius: '8px',
                        padding: '6px 20px',
                        minWidth: 120,
                        backgroundColor: (assessment.status === AssessmentStatus.DRAFT || assessment.status === AssessmentStatus.ARCHIVED) ? '#059669' : '#dc2626',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                        backgroundColor: (assessment.status === AssessmentStatus.DRAFT || assessment.status === AssessmentStatus.ARCHIVED) ? '#047857' : '#b91c1c',
                        transform: 'translateY(-1px)',
                        boxShadow: (assessment.status === AssessmentStatus.DRAFT || assessment.status === AssessmentStatus.ARCHIVED)
                            ? '0 4px 8px rgba(5, 150, 105, 0.3)'
                            : '0 4px 8px rgba(220, 38, 38, 0.3)',
                        },
                    }}
                    >
                    {toggling
                        ? 'Processing...'
                        : (assessment.status === AssessmentStatus.DRAFT || assessment.status === AssessmentStatus.ARCHIVED)
                        ? 'Publish'
                        : 'Unpublish'}
                </Button>

                    
                </Box>
            </Box>
            
             <Tabs
                value={currentTab}
                onChange={handleTabChange}
                textColor="primary"
                indicatorColor="primary"
                centered
                sx={{
                    '& .MuiTab-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        fontSize: '0.95rem',
                        py: 2,
                        px: 3,
                        minWidth: 120,
                        color: '#64748b',
                        '&.Mui-selected': {
                            color: '#3b82f6',
                            fontWeight: 600,
                        },
                    },
                    '& .MuiTabs-indicator': {
                        height: 3,
                        borderRadius: '2px 2px 0 0',
                        backgroundColor: '#3b82f6',
                    },
                    '& .MuiTabs-flexContainer': {
                        gap: 1,
                    }
                }}
            >
                {tabs.map((tab) => (
                    <Tab 
                        key={tab.value} 
                        label={tab.label} 
                        value={tab.value}
                    />
                ))}
            </Tabs>
            
            <Box sx={{ backgroundColor: "#c6e1f5", padding: 2 }}>
                {mountedTabs.includes("settings") && (
                    <Box sx={{ display: currentTab === "settings" ? 'block' : 'none' }}>
                        <Settings setSaved={setSaved} assessment={assessment} />
                    </Box>
                )}
                {mountedTabs.includes("questions") && (
                    <Box sx={{ display: currentTab === "questions" ? 'block' : 'none' }}>
                        <Items setSaved={setSaved} assessment={assessment} />
                    </Box>
                )}
                {mountedTabs.includes("responses") && (
                    <Box sx={{ display: currentTab === "responses" ? 'block' : 'none' }}>
                        <Responses assessment={assessment} />
                    </Box>
                )}
                
            </Box>

           
          
            <FirstPublishDialog
                open={firstPublishDialog}
                setOpen={setFirstPublishDialog}
                updateFunction={toggleAssessmentStatus}
            />
            
        </Box>
    );
}

export default (AssessmentDetail);